import { memo, useMemo } from 'react';
import { faArrowsRotate, faCheck, faTrash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/modules/Button';
import { Icon } from '@/modules/Icon';
import { ProgressBar } from '@/modules/ProgressBar';
import { FileTypeIcon } from '../atoms/FileTypeIcon';
import type { FileUploadListItemData, FileUploadListRowSize } from '../../types';

const DEFAULT_ERROR = 'Error al cargar el archivo.';

export interface FileUploadListItemProps {
  /** Datos de la fila (nombre, estado, progreso, etc.). */
  item: FileUploadListItemData;
  /** `lg` como fila ancha en Figma; `sm` compacta (sin barra al completar). */
  rowSize?: FileUploadListRowSize;
  /** Eliminar esta entrada (siempre; en error va antes del reintento). */
  onRemove?: (id: string) => void;
  /**
   * Reintentar subida tras error (red, 5xx, validación remota, etc.).
   * Solo en `status === 'error'`: botón con `faArrowsRotate` junto al cierre (X).
   */
  onRetry?: (id: string) => void;
  /** Eliminar archivo completado (papelera). */
  onDelete?: (id: string) => void;
  className?: string;
}

/**
 * Fila de estado de carga: icono de tipo, título, detalle, barra solo mientras `uploading`,
 * y acciones (cerrar, reintentar, papelera, check en completo) según Figma *Loader*.
 *
 * @example
 * ```tsx
 * <FileUploadListItem
 *   item={{
 *     id: '1',
 *     fileName: 'informe.png',
 *     format: 'png',
 *     status: 'uploading',
 *     progress: 35,
 *     detailLine: '3.4 Mb | 35%',
 *     secondsRemaining: 5,
 *   }}
 *   onRemove={(id) => {}}
 * />
 * ```
 *
 * Error de red o servidor: `status: 'error'`, `errorMessage` y `onRetry` para el botón con
 * `faArrowsRotate` junto al de quitar.
 */
export const FileUploadListItem = memo(function FileUploadListItem({
  item,
  rowSize = 'lg',
  onRemove,
  onRetry,
  onDelete,
  className,
}: FileUploadListItemProps) {
  const iconSize = rowSize === 'sm' ? 'md' : 'lg';

  const secondaryClass = useMemo(
    () =>
      cn(
        rowSize === 'sm'
          ? 'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs'
          : 'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
        item.status === 'error' ? 'ui-text-danger' : 'ui-text-secondary',
      ),
    [item.status, rowSize],
  );

  const titleClass =
    rowSize === 'sm'
      ? 'ui-text-body-xs-bold ui-font-body-xs-bold ui-leading-body-xs-bold ui-tracking-body-xs ui-text-primary'
      : 'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ui-text-primary';

  const detailText = useMemo(() => {
    if (item.status === 'error') {
      return item.errorMessage ?? DEFAULT_ERROR;
    }
    const base = item.detailLine ?? '';
    if (
      item.status === 'uploading' &&
      item.secondsRemaining != null &&
      item.secondsRemaining >= 0
    ) {
      if (base.includes('restantes')) {
        return base || '\u00a0';
      }
      const sec = Math.ceil(item.secondsRemaining);
      const eta = sec <= 0 ? 'finalizando…' : `${sec} seg. restantes`;
      if (!base.trim()) return eta;
      return `${base} • ${eta}`;
    }
    return base || '\u00a0';
  }, [item.status, item.detailLine, item.secondsRemaining, item.errorMessage]);

  /** Solo en subida; en `complete` no se muestra barra (check + acciones bastan). */
  const showProgressBar = item.status === 'uploading';

  const progressValue = item.progress ?? 0;

  return (
    <div
      className={cn(
        'ui-flex ui-w-full ui-flex-col ui-gap-8 ui-rounded-control-sm ui-border ui-border-default ui-bg-surface-default ui-p-8',
        className,
      )}
    >
      <div className="ui-flex ui-w-full ui-items-start ui-gap-12">
        <FileTypeIcon format={item.format} size={iconSize} />

        <div className="ui-flex ui-min-h-px ui-min-w-0 ui-flex-1 ui-flex-col">
          <p className={cn('ui-truncate', titleClass)} title={item.fileName}>
            {item.fileName}
          </p>
          <p className={secondaryClass}>{detailText}</p>
        </div>

        <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-4">
          {item.status === 'complete' && rowSize === 'lg' && (
            <span
              className="ui-flex ui-size-20 ui-items-center ui-justify-center ui-rounded-full ui-bg-fill-success-default"
              aria-hidden
            >
              <Icon name={faCheck} size="xs" color="white" />
            </span>
          )}
          {item.status === 'error' && onRetry && (
            <Button
              theme="solid"
              color="tertiary"
              size="xxs"
              pill="pill"
              icon={faArrowsRotate}
              onClick={(e) => {
                e.stopPropagation();
                onRetry(item.id);
              }}
              aria-label="Reintentar carga"
            />
          )}

          {item.status === 'error' && onRemove && (
            <Button
              theme="solid"
              color="tertiary"
              size="xxs"
              pill="pill"
              icon={faXmark}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.id);
              }}
              aria-label="Quitar de la lista"
            />
          )}

          {item.status !== 'error' && onRemove && (
            <Button
              theme="solid"
              color="tertiary"
              size="xxs"
              pill="pill"
              icon={faXmark}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.id);
              }}
              aria-label="Quitar de la lista"
            />
          )}

          {item.status === 'complete' && onDelete && (
            <Button
              theme="solid"
              color="tertiary"
              size="xxs"
              pill="pill"
              icon={faTrash}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item.id);
              }}
              aria-label="Eliminar archivo"
            />
          )}
        </div>
      </div>

      {showProgressBar && (
        <ProgressBar
          progress={progressValue}
          color="brand"
          size="xxs"
          showLabel={false}
          showProgress={false}
          showCloseButton={false}
          compact={false}
        />
      )}
    </div>
  );
});

FileUploadListItem.displayName = 'FileUploadListItem';
